import { FRONTEND_FLAGS } from "../core/components/LaunchDarklyProvider";
import { Site, SiteFeatureFlag, TestValues } from "../resources/modelTypes";
import { useCurrentSite } from "./models/SiteContext";
import { useMediaQueryState } from "@minted/minted-components";
import { useLDClient } from "launchdarkly-react-client-sdk";

/**
 * Hook to check if the reworked OI guest dashboard is enabled for this site.
 * The flag value is captured at site creation time and stored in feature_flags.
 */
export const useOIReworkedDashboardEnabled = (): boolean => {
  const site = useCurrentSite();
  console.log("site", site);
  return (
    site.featureFlags[SiteFeatureFlag.oiDashboardV2] === TestValues.Experiment
  );
};

/**
 * Hook to check if the OI Messages tab is enabled for this site.
 * The flag value is captured at site creation time and stored in feature_flags.
 */
export const useOIMessagesTabEnabled = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.OiMessagesTab] === TestValues.Experiment
  );
};

/**
 * Hook to check if the redesigned OI mobile navigation is enabled for this site.
 * The flag value is captured at site creation time and stored in feature_flags.
 */
export const useOIMobileNavigationEnabled = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.OiMobileNavigation] ===
    TestValues.Experiment
  );
};

/**
 * Whether the redesigned header and sidebar navigation is enabled for a site.
 */
export const isWWHeaderAndSidebarEnabled = (site: Site): boolean => {
  if (!site.isWedding) return false;

  return (
    site.featureFlags[SiteFeatureFlag.WWHeaderAndSidebar] ===
    TestValues.Experiment
  );
};

/**
 * Hook to check if the redesigned header and sidebar navigation is enabled
 * for this user.
 */
export const useWWHeaderAndSidebar = (): boolean => {
  const site = useCurrentSite();
  return isWWHeaderAndSidebarEnabled(site);
};

/**
 * Whether the new home page layout experience is enabled for a site.
 *
 * The Layout and Fonts & Colors views this unlocks are only reachable from the
 * redesigned sidebar, so the flag is inert without it.
 */
export const isNewLayoutsEnabled = (site: Site): boolean =>
  isWWHeaderAndSidebarEnabled(site) &&
  site.featureFlags[SiteFeatureFlag.NewLayouts] === TestValues.Experiment;

/**
 * Hook to check if the new home page layout experience is enabled for this site.
 */
export const useNewLayouts = (): boolean => {
  const site = useCurrentSite();
  return isNewLayoutsEnabled(site);
};

/**
 * Custom hooks to get experience flags.
 * They must be used inside a LaunchDarklyProvider.
 */
export const useNewMobileExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  return mediumMediaQueryState === "BELOW" && site.isOnlineInvitation;
};

export const useNewDesktopExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });

  if (!site.isOnlineInvitation) {
    return false;
  }

  if (mediumMediaQueryState === "BELOW") {
    return false;
  }

  return (
    site.featureFlags[SiteFeatureFlag.SectionEditorExperience] ===
    TestValues.Experiment
  );
};

export const useGuestHouseholdMembersFlag = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.ConnectHouseholdMembers] ===
    TestValues.Experiment
  );
};

export const useStripeRegistries = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.StripeRegistry] === TestValues.Experiment
  );
};

export const useIsClickToEditEnabled = (): boolean => {
  const site = useCurrentSite();
  const ldClient = useLDClient();
  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });

  if (site.isOnlineInvitation) {
    return true;
  } else if (smallMediaQueryState === "BELOW") {
    return false;
  } else {
    // WW click-to-edit is desktop only
    const lowerCutoffDate = new Date("2026-01-27T12:00:00");
    const upperCutoffDate = new Date("2026-03-05T12:00:00");
    const siteCreatedAt = new Date(site.createdAt);

    // Include only sites created between cutoff dates in experiment
    if (siteCreatedAt < lowerCutoffDate || siteCreatedAt > upperCutoffDate) {
      return false;
    }

    const flagValue = ldClient
      ? ldClient.variation(FRONTEND_FLAGS.weddingClickToEdit, false)
      : false;

    return flagValue;
  }
};

/**
 * Hook to check if automatic party-size parsing from contact names is enabled.
 */
export const useAutoParseContactNameFlag = (): boolean => {
  const site = useCurrentSite();
  const ldClient = useLDClient();
  if (site.isOnlineInvitation) return false;
  return ldClient
    ? ldClient.variation(FRONTEND_FLAGS.autoParseContactName, false) ===
        TestValues.Experiment
    : false;
};
